import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "./Input";
import { Button } from "./Button";

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  options?: string[];
}

/**
 * Simple list editor for string arrays — used for keywords, factions, etc.
 * Provides Add input + chips that can be removed. If options[] provided, also
 * allows quick-add via select.
 */
export function StringList({ value, onChange, placeholder, options }: Props) {
  const [draft, setDraft] = useState("");

  const add = (v: string) => {
    const t = v.trim();
    if (!t) return;
    if (value.includes(t)) return;
    onChange([...value, t]);
    setDraft("");
  };

  const remove = (v: string) => onChange(value.filter((x) => x !== v));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {value.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full border border-[#2a2f3a] bg-[#22262f] px-2 py-0.5 text-xs"
          >
            {v}
            <button
              type="button"
              onClick={() => remove(v)}
              className="text-[#64748b] hover:text-red-400"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {value.length === 0 && (
          <span className="text-xs text-[#64748b] italic">none</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(draft);
            }
          }}
          placeholder={placeholder ?? "Add…"}
        />
        <Button type="button" variant="secondary" onClick={() => add(draft)}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {options && options.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {options
            .filter((o) => !value.includes(o))
            .map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => add(o)}
                className="rounded-full border border-[#2a2f3a] bg-transparent px-2 py-0.5 text-[11px] text-[#64748b] hover:border-amber/50 hover:text-amber-soft"
              >
                + {o}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
